import React from 'react';

import { composeTrackedClick } from '@/services/analytics';

/**
 * HIPAA: `name`/`props` must be opaque ids/enums only — never free text, DOM text, or PHI.
 * `props` values follow the same rule as `TrackContext.props` everywhere else in the SDK.
 *
 * Caveat: the single child must forward `onClick` to a real DOM element (most shadcn/ui
 * components, e.g. `Button`, do). For a component that doesn't accept/forward `onClick`, track
 * the interaction via `useTrackEvent()` instead.
 *
 * Do NOT also put `data-analytics-id` on the same element as `<TrackClick>` — the global
 * capture-phase listener would fire a second CLICK event for the same interaction (double-count).
 */
interface TrackClickProps {
  /** Click event name — also used as `target_id`. `domain:entity[:sub]:action`, e.g. "case:save". */
  name: string;
  /** Additional non-PHI context. Values must be opaque ids/enums only. */
  props?: Record<string, string>;
  caseRecordId?: string;
  programId?: string;
  /** Exactly one React element that accepts/forwards an `onClick` prop. */
  children: React.ReactElement;
}

/**
 * Wraps a single child element to fire a typed CLICK analytics event on click, composed with
 * the child's own `onClick` (which always runs first). Adds no extra DOM node, so it never
 * breaks flex/grid layouts.
 */
export function TrackClick({ name, props, caseRecordId, programId, children }: TrackClickProps) {
  const child = React.Children.only(children);
  const trackedOnClick = composeTrackedClick(
    (child.props as { onClick?: (e: React.MouseEvent) => void }).onClick,
    name,
    { targetId: name, props, caseRecordId, programId },
  );

  return React.cloneElement(child, { onClick: trackedOnClick } as Partial<
    React.ComponentProps<'button'>
  >);
}
